import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import NewsbannerImg from '@public/images/news/news-banner.png';

import Button from '@/components/Button';
import ArrowDown from '@/components/Icon/ArrowDown';
import Eye from '@/components/Icon/Eye';
import NoData from '@/components/NoData';
import Title from '@/components/Title';

import { MyContext } from '@/config/context-manager';
import { blogPostsResponse } from '@/request/interface/news';
import { getBlogPosts } from '@/request/news';

const News: React.FC = () => {
  const { t } = useTranslation(['news', 'common']);
  const { locale } = useContext(MyContext);
  const [posts, setPosts] = useState<blogPostsResponse['data']['data']>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const listref = useRef<HTMLDivElement>(null);

  const getCategory = useMemo(() => {
    if (process.env.NEXT_PUBLIC_STAGE === 'prod') {
      if (locale === 'en') {
        return 'press-en';
      }
      return 'press-zh';
    }
    if (locale === 'en') {
      return 'Chinese Phrases';
    }
    return 'chinese-learning-zh';
  }, [locale]);

  const fetchPosts = useCallback(
    async (initial = false) => {
      try {
        setIsLoading(true);
        // eslint-disable-next-line no-underscore-dangle
        const _page = initial ? 1 : page + 1;
        const { data } = await getBlogPosts({
          page: _page,
          per_page: 9,
          category: getCategory,
        });
        if (data.pagination.total_pages > _page) {
          setHasMore(true);
        } else {
          setHasMore(false);
        }
        if (initial) {
          setPosts(data.data || []);
        } else {
          setPosts((prev) => [...prev, ...(data.data || [])]);
        }
        setPage(_page);
      } catch (error) {
        console.error('Failed to fetch posts:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [page, getCategory],
  );

  useEffect(() => {
    fetchPosts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  const goNewsDetail = (link: string) => {
    window.open(link, '_blank');
  };

  useEffect(() => {
    fetchPosts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="m-auto mb-[80px] mt-[105px] w-full px-5 lg:mb-[180px] lg:mt-[145px] lg:w-[984px] xl:mb-[260px] xl:mt-[163px] xl:w-[1400px]">
      <div className="flex w-full flex-col justify-stretch rounded-3xl bg-wk-bg-2 bg-[url('/images/news/news-banner-adorn-2.png')] bg-[length:200px_100px] bg-no-repeat p-[10px] lg:flex-row lg:bg-[url('/images/news/news-banner-adorn.png')] lg:bg-[length:354px_190px] lg:bg-left-bottom lg:px-10 lg:py-6 xl:rounded-4xl xl:bg-[length:464px_250px] xl:px-15 xl:py-[30px]">
        <div className="relative mt-[10px] flex flex-1 flex-col text-center lg:ml-[27px] lg:mt-[32px] lg:items-start xl:ml-[60px] xl:mt-[55px]">
          <Title theme="colorful" className="text-cente lg:text-left [&]:lg:mx-0">
            <strong>{t('新闻中心')}</strong>
          </Title>
          <div className="mb-5 mt-3 text-[14px] font-normal text-wk-words-2 lg:mt-4 xl:mt-6 xl:text-[20px]">
            {t('新闻中心-副标题')}
          </div>
          <div
            onClick={() => {
              window.scrollTo({
                top: (listref.current?.offsetTop || 0) - 150,
                behavior: 'smooth',
              });
            }}
            className="text-wk-words absolute bottom-0 left-0 hidden cursor-pointer rounded-full border border-[#E4E5EB] p-[14px] hover:shadow-wk1 lg:block xl:p-5"
          >
            <ArrowDown />
          </div>
        </div>
        <Image
          width={640}
          height={360}
          src={NewsbannerImg}
          className="xl:rounded-5 h-[177px] w-[315px] rounded-[9px] lg:h-[254px] lg:w-[452px] lg:rounded-[14px] xl:h-[360px] xl:w-[640px]"
          alt="newsbanner"
        />
      </div>
      <div className="mt-10 lg:mt-15 xl:mt-20" ref={listref}>
        {posts.length === 0 ? (
          <div className="relative flex min-h-[300px] items-center justify-center">
            <NoData text={t('暂无新闻')} />
          </div>
        ) : (
          <div className="grid cursor-pointer grid-cols-1 gap-y-3 lg:grid-cols-3 lg:gap-x-4 lg:gap-y-6 xl:gap-x-6 xl:gap-y-8">
            {posts.map((post) => (
              <div
                onClick={() => goNewsDetail(post.link)}
                key={post.id}
                className="flex cursor-pointer flex-col rounded-3xl bg-white shadow-wk1 hover:shadow-wk1Hover xl:rounded-5xl"
              >
                <div
                  className="h-[189px] w-full rounded-t-3xl bg-cover bg-center object-cover lg:h-[179px] xl:h-[254px] xl:rounded-5xl"
                  style={{ backgroundImage: `url(${post.featured_image || '/images/news/default-cover.png'})` }}
                />
                <div className="m-4 mt-3 flex flex-1 flex-col justify-between text-[14px] font-normal xl:m-6 xl:mt-4">
                  <div>
                    <div className=" line-clamp-2 font-semibold text-wk-words-1 lg:text-[16px] xl:text-[20px]">
                      {post.title}
                    </div>
                    <div className="mt-2 line-clamp-2 text-wk-words-3 lg:text-[14px] xl:text-[16px]">
                      {post.preview}
                    </div>
                  </div>
                  <div>
                    {post.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 lg:mt-3 xl:mt-4">
                        {post.tags.map((tag) => {
                          const maxLength = locale === 'en' ? 20 : 8;
                          const truncatedTag = tag.length > maxLength ? `${tag.substring(0, maxLength)}...` : tag;
                          return (
                            <div key={tag} className=" rounded-full bg-wk-bg-1 px-2 py-1 text-xs text-wk-words-2">
                              {truncatedTag}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className="mt-3 flex items-center justify-between text-[12px] lg:mt-3 xl:mt-4 xl:text-[14px]">
                      <div className="flex items-center text-wk-words-3">
                        <Eye className="mr-1" />
                        {post.views || 0}
                      </div>
                      <div className="text-wk-words-3">{post.date}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {hasMore && (
          <div className="flex justify-center">
            <Button
              onClick={() => fetchPosts()}
              size="xl"
              className="mt-6 lg:mt-12 xl:mt-[60px] [&]:text-base [&]:font-medium [&]:xl:text-xl"
              traceName="O_WhyWuKong_BlogModule_MoreClick"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg
                    className="mr-2 h-5 w-5 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  {t('common:加载中')}
                </div>
              ) : (
                t('news:加载更多')
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default News;
